import type {
  EnvironmentId,
  PullRequestDetail,
  PullRequestRef,
  PullRequestReviewerCandidate,
} from "@cmmd-forge/contracts";
import { useQuery } from "@tanstack/react-query";
import { CheckIcon, UserPlusIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Menu, MenuPopup, MenuTrigger } from "~/components/ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { cmmdApiClient } from "~/environments/cmmdApi";
import {
  commandPullRequestRef,
  pullRequestReviewerCandidatesQueryOptions,
} from "~/lib/pullRequestReactQuery";
import { usePullRequestActivityCommand } from "../timeline/usePullRequestActivityCommand";
import { PullRequestSummaryReviewers } from "./PullRequestSummary.renderers";
import type { PullRequestReviewerEntry } from "./pullRequestSummary.logic";

export function PullRequestReviewers({
  environmentId,
  reference,
  detail,
  reviewers,
  reviewerEntries,
  isDetailFresh,
}: {
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
  readonly detail: PullRequestDetail;
  readonly reviewers?: PullRequestDetail["reviewers"];
  readonly reviewerEntries?: ReadonlyArray<PullRequestReviewerEntry>;
  readonly isDetailFresh: boolean;
}) {
  const supported =
    detail.capabilities.reviewers.request && detail.capabilities.reviewers.listCandidates;
  return (
    <PullRequestSummaryReviewers
      reviewers={reviewers ?? detail.reviewers}
      {...(reviewerEntries === undefined ? {} : { reviewerEntries })}
      action={
        supported ? (
          <PullRequestReviewerPicker
            environmentId={environmentId}
            reference={reference}
            allowed={isDetailFresh && detail.viewerPermissions.requestReviewers}
          />
        ) : undefined
      }
    />
  );
}

function matches(candidate: PullRequestReviewerCandidate, query: string) {
  const needle = query.trim().toLowerCase();
  return (
    needle.length === 0 ||
    candidate.login.toLowerCase().includes(needle) ||
    (candidate.name ?? "").toLowerCase().includes(needle)
  );
}

function PullRequestReviewerPicker({
  environmentId,
  reference,
  allowed,
}: {
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
  readonly allowed: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const command = usePullRequestActivityCommand(environmentId, reference);
  const candidatesQuery = useQuery({
    ...pullRequestReviewerCandidatesQueryOptions({ environmentId, reference }),
    enabled: open && allowed,
  });
  if (!allowed) return <DisabledReviewerPicker />;

  const candidates = (candidatesQuery.data?.candidates ?? []).filter((candidate) =>
    matches(candidate, query),
  );
  return (
    <Menu open={open} onOpenChange={setOpen}>
      <MenuTrigger
        render={
          <Button size="icon-xs" variant="ghost" aria-label="Request a review">
            <UserPlusIcon className="size-3.5" />
          </Button>
        }
      />
      <MenuPopup align="start" side="bottom" className="w-72 p-0">
        <div className="border-b border-border/60 p-2">
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder="Search people with access"
            aria-label="Search people with access"
            size="sm"
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          <PullRequestReviewerCandidateState
            candidates={candidates}
            pending={candidatesQuery.isPending}
            error={candidatesQuery.error}
            query={query}
            command={command}
            reference={reference}
          />
          {candidatesQuery.data?.truncated ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              This repository has more people with access than are listed here. Ask for the rest on
              the host.
            </p>
          ) : null}
        </div>
      </MenuPopup>
    </Menu>
  );
}

function DisabledReviewerPicker() {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button size="icon-xs" variant="ghost" disabled aria-label="Request a review">
            <UserPlusIcon className="size-3.5" />
          </Button>
        }
      />
      <TooltipPopup side="bottom">
        Asking someone to review needs write access on this repository
      </TooltipPopup>
    </Tooltip>
  );
}

function PullRequestReviewerCandidateState({
  candidates,
  pending,
  error,
  query,
  command,
  reference,
}: {
  readonly candidates: ReadonlyArray<PullRequestReviewerCandidate>;
  readonly pending: boolean;
  readonly error: Error | null;
  readonly query: string;
  readonly command: ReturnType<typeof usePullRequestActivityCommand>;
  readonly reference: PullRequestRef;
}) {
  if (pending) return <p className="p-2 text-xs text-muted-foreground">Loading people…</p>;
  if (error !== null)
    return (
      <p className="p-2 text-xs text-muted-foreground">
        The people with access could not be read. {error.message}
      </p>
    );
  if (candidates.length === 0)
    return (
      <p className="p-2 text-xs text-muted-foreground">
        {query.length > 0
          ? "Nobody with access matches that."
          : "Nobody else has access to this repository."}
      </p>
    );
  return candidates.map((candidate) => (
    <PullRequestReviewerCandidateRow
      key={`${candidate.kind}:${candidate.id}`}
      candidate={candidate}
      command={command}
      reference={reference}
    />
  ));
}

function PullRequestReviewerCandidateRow({
  candidate,
  command,
  reference,
}: {
  readonly candidate: PullRequestReviewerCandidate;
  readonly command: ReturnType<typeof usePullRequestActivityCommand>;
  readonly reference: PullRequestRef;
}) {
  return (
    <button
      type="button"
      disabled={command.isPending}
      onClick={() =>
        command.mutate(async () => {
          await cmmdApiClient.requestPullRequestReviewers({
            ...commandPullRequestRef(reference),
            idempotencyKey: crypto.randomUUID(),
            reviewers: [{ id: candidate.id, kind: candidate.kind }],
            requested: !candidate.isRequested,
          });
        })
      }
      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-accent/60 disabled:opacity-60"
    >
      <span className="min-w-0 flex-1 truncate">{candidate.name ?? candidate.login}</span>
      {candidate.kind === "team" ? (
        <span className="shrink-0 text-muted-foreground">team</span>
      ) : null}
      {candidate.isRequested ? (
        <CheckIcon aria-label="Already asked" className="size-3.5 shrink-0" />
      ) : null}
    </button>
  );
}
